/* eslint-disable no-unused-vars */
const THREE = window.THREE;
import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { readStoredViewerBackground, applyViewerBackground } from '@/utils/viewerBackground';
import {
  ForgeEquipment,
  ForgeAnnotation,
  ForgeInspection,
  ForgeScaffold,
  ForgeAttachment,
  UserSettings,
  DisplaySettings,
  PointCloudDisplay,
  PointCloudDownload,
  ForgePageMarkup,
  ForgeCamera,
  PointCloudModelInfo,
  ForgeModelVersion,
  ForgePipeline,
  ForgeStructural,
  ForgeAvaInspect,
  QuickSettingsDisplay,
} from './../scripts/js/forgeModels';
import HttpService from './httpService';
import { FACILITY_REQUEST } from './http/RequestRegistry';
import { getAllParentsIds, setModelVisibility } from './forgeUtils';
import { POSITION_PICK_SOURCE } from '@/constants/positionPickSource';

function facilityHeaders() {
  const facilityId = store.state.assetId;
  return facilityId ? { 'X-Facility-Id': facilityId } : {};
}

export const editType = {
  CREATE_TAG: 'CreateTag',
  EDIT_TAG: 'EditTag',
  DELETE_TAGS: 'DeleteTag',

  CREATE_PID: 'CreatePID',
  EDIT_PID: 'EditPID',
  DELETE_PID: 'DeletePID',

  CREATE_Project: 'CreateProject',
  EDIT_Project: 'EditProject',
  DELETE_Project: 'DeleteProject',

  EDIT_IMAGE360: 'EditImage360',
  DELETE_IMAGES360: 'DeleteImages360',

  CREATE_ANNOTATION: 'CreateAnnotation',
  EDIT_ANNOTATION: 'EditAnnotation',
  DELETE_ANNOTATIONS: 'DeleteAnnotations',

  CREATE_AMIS: 'CreateAMIS',
  EDIT_AMIS: 'EditAMIS',
  DELETE_AMIS: 'DeleteAMIS',

  CREATE_INSPECTION: 'CreateInspection',
  EDIT_INSPECTION: 'EditInspection',
  DELETE_INSPECTION: 'DeleteInspection',

  CREATE_SCAFFOLD: 'CreateScaffold',
  EDIT_SCAFFOLD: 'EditScaffold',
  SET_SCAFFOLD_POSITION: 'SetScaffoldPosition',
  CLEAR_SCAFFOLD_POSITION: 'ClearScaffoldPosition',
  DELETE_SCAFFOLDS: 'DeleteScaffolds',

  CREATE_CAMERA: 'CreateCamera',
  DELETE_CAMERAS: 'DeleteCameras',

  CREATE_AVA_INSPECT: 'CreateAvaInspect',
  EDIT_AVA_INSPECT: 'EditAvaInspect',
  DELETE_AVA_INSPECT: 'DeleteAvaInspect',
};

export function saveTags(obj, changeType, img64, callback, onError) {
  let dbIds = (obj.children ?? []).map((e) => e.dbId);
  let dbIdChain = [];
  if (store.state.viewer) {
    dbIdChain = dbIds.reduce((acc, dbId) => {
      let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
      return acc.concat(chain);
    }, []);
  }

  switch (changeType) {
    case editType.CREATE_TAG:
      postDataAsync(
        'api/equipments/createEquipment',
        { assetId: store.state.assetId, tag: obj, dbIdChain, img64: img64 },
        (tag) => {
          obj = Object.assign(new ForgeEquipment(), tag);
          obj.parseContent();
          if (callback) callback(obj);
        },
        onError,
      );
      break;
    case editType.EDIT_TAG:
      postDataAsync(
        'api/equipments/editEquipment',
        { assetId: store.state.assetId, tag: obj, img64: img64 },
        (tag) => {
          obj = Object.assign(new ForgeEquipment(), tag);
          obj.parseContent();
          if (callback) callback(obj);
        },
        onError,
      );
      break;
    case editType.DELETE_TAGS:
      postDataAsync(
        'api/equipments/deleteEquipment',
        { assetId: store.state.assetId, tagsGuid: obj.map((o) => o.id) },
        () => {
          if (callback) callback();
        },
        onError,
      );
      break;
  }
}
export function savePIDs(obj, changeType) {
  switch (changeType) {
    case editType.CREATE_PID:
      postDataAsync('api/pdis/createPid', { assetId: store.state.assetId, pid: obj }, (id) => {
        obj.id = id;
      });
      break;
    case editType.EDIT_PID:
      postDataAsync('api/pids/editPId', { assetId: store.state.assetId, pid: obj });
      break;
    case editType.DELETE_PID:
      postDataAsync(`api/pids/delete?id=${obj.id}`, null);
      break;
  }
}
export function saveProjects(obj, changeType, args1) {
  switch (changeType) {
    case editType.CREATE_Project:
      postDataAsync(
        'api/forge/projects/createproject',
        { assetId: store.state.assetId, project: obj },
        (id) => {
          obj.id = id;
        },
      );
      break;
    case editType.EDIT_Project:
      postDataAsync('api/forge/projects/editproject', {
        assetId: store.state.assetId,
        project: obj,
      });
      break;
    case editType.DELETE_Project:
      postDataAsync('api/forge/projects/deleteproject?id=' + obj.id, null);
      break;

    case editType.EDIT_IMAGE360:
      postDataAsync('api/images360/editImage360', {
        assetId: store.state.assetId,
        image: obj,
        levelName: args1,
      });
      break;
    case editType.DELETE_IMAGES360:
      postDataAsync('api/images360/deleteImages360', {
        assetId: store.state.assetId,
        ids: obj.map((o) => o.id),
      });
      break;
  }
}
export function saveAnnotations(obj, changeType, img64, callback, onError) {
  let dbIds = (obj.children ?? []).map((e) => e.dbId);
  let dbIdChain = [];
  if (store.state.viewer) {
    dbIdChain = dbIds.reduce((acc, dbId) => {
      let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
      return acc.concat(chain);
    }, []);
  }
  const headers = facilityHeaders();
  switch (changeType) {
    case editType.CREATE_ANNOTATION:
      postDataAsync(
        'api/annotations/createAnnotation',
        { assetId: store.state.assetId, annotation: obj, dbIdChain, img64: img64 },
        (anno) => {
          obj = Object.assign(new ForgeAnnotation(), anno);
          obj.parseContent();
          if (callback) callback(obj);
        },
        (error) => {
          if (onError) onError(error);
        },
        undefined,
        undefined,
        headers,
      );
      break;
    case editType.EDIT_ANNOTATION:
      postDataAsync(
        'api/annotations/editAnnotation',
        { assetId: store.state.assetId, annotation: obj, img64: img64 },
        (anno) => {
          anno = Object.assign(new ForgeAnnotation(), anno);
          anno.parseContent();
          obj.thumbnailUrl = anno.thumbnailUrl;
          if (callback) callback(anno);
        },
        (error) => {
          if (onError) onError(error);
        },
        undefined,
        undefined,
        headers,
      );
      break;
    case editType.DELETE_ANNOTATIONS:
      postDataAsync(
        'api/annotations/deleteAnnotations',
        { assetId: store.state.assetId, guids: obj.map((o) => o.id) },
        () => {
          if (callback) callback();
        },
        (error) => {
          if (onError) onError(error);
        },
        undefined,
        undefined,
        headers,
      );
      break;
  }
}
export function saveAMIS(obj, changeType, img64, callback, onError) {
  let dbIds = (obj.children ?? []).map((e) => e.dbId);
  let dbIdChain = [];
  if (store.state.viewer) {
    dbIdChain = dbIds.reduce((acc, dbId) => {
      let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
      return acc.concat(chain);
    }, []);
  }

  switch (changeType) {
    case editType.CREATE_AMIS:
      postDataAsync(
        'api/AMIS/create',
        { assetId: store.state.assetId, inspection: obj, dbIdChain, img64: img64 },
        (anno) => {
          obj = Object.assign(new ForgeInspection(), anno);
          obj.parseContent();
          if (callback) callback();
        },
        onError,
      );
      break;
    case editType.EDIT_AMIS:
      postDataAsync(
        'api/AMIS/edit',
        { assetId: store.state.assetId, inspection: obj, img64: img64 },
        (anno) => {
          anno = Object.assign(new ForgeInspection(), anno);
          anno.parseContent();
          obj.setSameParameters(anno);
          if (callback) callback();
        },
        onError,
      );
      break;
    case editType.DELETE_AMIS:
      postDataAsync(
        'api/AMIS/delete',
        { assetId: store.state.assetId, guids: obj.map((o) => o.id) },
        () => {
          if (callback) callback();
        },
        onError,
      );
      break;
  }
}
export function saveInspections(obj, changeType, img64, callback, onError) {
  let dbIds = (obj.children ?? []).map((e) => e.dbId);
  let dbIdChain = [];
  if (store.state.viewer) {
    dbIdChain = dbIds.reduce((acc, dbId) => {
      let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
      return acc.concat(chain);
    }, []);
  }

  switch (changeType) {
    case editType.CREATE_INSPECTION:
      postDataAsync(
        'api/forge/inspections/createInspection',
        { assetId: store.state.assetId, inspection: obj, dbIdChain, img64: img64 },
        (anno) => {
          obj = Object.assign(new ForgeInspection(), anno);
          obj.parseContent();
          if (callback) callback(obj);
        },
        onError,
      );
      break;
    case editType.EDIT_INSPECTION:
      postDataAsync(
        'api/forge/inspections/editInspection',
        { assetId: store.state.assetId, inspection: obj, img64: img64 },
        (anno) => {
          anno = Object.assign(new ForgeInspection(), anno);
          anno.parseContent();
          obj.setSameParameters(anno);
          if (callback) callback(anno);
        },
        (error) => {
          if (onError) onError(error);
        },
      );
      break;
    case editType.DELETE_INSPECTION:
      postDataAsync(
        'api/forge/inspections/deleteInspections',
        { assetId: store.state.assetId, guids: obj.map((o) => o.id) },
        () => {
          if (callback) callback();
        },
        (error) => {
          if (onError) onError(error);
        },
      );
      break;
  }
}
export function saveCamera(obj, changeType, arg, callback, onError) {
  switch (changeType) {
    case editType.CREATE_CAMERA:
      postDataAsync(
        'api/forge/dashboard/createCamera',
        { assetId: store.state.assetId, camera: obj, thumbnailBase64: arg },
        callback,
        onError,
      );
      break;
    case editType.DELETE_CAMERAS:
      postDataAsync(
        'api/forge/dashboard/deleteCamera',
        { assetId: store.state.assetId, cameraGuids: obj.map((o) => o.id) },
        callback,
        onError,
      );
      break;
  }
}
/** A THREE.Vector3 or a plain projection, as the API's `{x,y,z}`; null when the tag has none. */
function toVector3Payload(vector) {
  if (!vector) return null;
  return { x: vector.x, y: vector.y, z: vector.z };
}

export class ScaffoldWriteRefreshError extends Error {
  constructor({ operation, tagId, facilityId, response, cause }) {
    super('Scaffold write succeeded but client reconciliation failed');
    this.name = 'ScaffoldWriteRefreshError';
    this.operation = operation;
    this.tagId = tagId ?? response?.id ?? null;
    this.facilityId = facilityId;
    this.response = response;
    this.cause = cause;
    this.writeSucceeded = true;
  }
}

function scaffoldWriteRefreshError(operation, obj, facilityId, response, cause) {
  return new ScaffoldWriteRefreshError({
    operation,
    tagId: response?.id ?? obj?.id,
    facilityId,
    response,
    cause,
  });
}

/**
 * @param {object} options - extra fields for the narrow commands. `SET_SCAFFOLD_POSITION` reads
 *   `{ source, imageId }` off the position-assignment payload; the other cases ignore it.
 * @returns {Promise<ForgeScaffold>|undefined} `SET_SCAFFOLD_POSITION` and
 *   `CLEAR_SCAFFOLD_POSITION` return a promise - it resolves with the hydrated tag and rejects on
 *   failure, and they do not use `callback` or `onError`. Their callers have to await the write
 *   inside their own try/catch/finally, which a callback-style save cannot give them. Every other
 *   case stays callback-style, unchanged.
 */
export function saveScaffolds(
  obj,
  changeType,
  img64,
  callback,
  onError,
  options = {},
) {
  const assetId = options.assetId ?? store.state.assetId;
  let dbIds = (obj.children ?? []).map((e) => e.dbId);
  let dbIdChain = [];
  if (store.state.viewer) {
    dbIdChain = dbIds.reduce((acc, dbId) => {
      let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
      return acc.concat(chain);
    }, []);
  }

  switch (changeType) {
    case editType.CREATE_SCAFFOLD:
      postDataAsync(
        'api/scaffolds/createScaffold',
        { assetId, scaffold: obj, dbIdChain, img64: img64 },
        (scaffold) => {
          try {
            obj = Object.assign(new ForgeScaffold(), scaffold);
            obj.parseContent();
            if (callback) callback(obj);
          } catch (err) {
            onError?.(scaffoldWriteRefreshError('create', obj, assetId, scaffold, err));
          }
        },
        onError,
      );
      break;
    case editType.EDIT_SCAFFOLD:
      postDataAsync(
        'api/scaffolds/editScaffold',
        { assetId: store.state.assetId, scaffold: obj, img64: img64 },
        (scaffold) => {
          let tag = Object.assign(new ForgeScaffold(), scaffold);
          tag.parseContent();
          obj.thumbnailUrl = tag.thumbnailUrl;
          if (callback) callback(tag);
        },
        onError,
      );
      break;
    case editType.SET_SCAFFOLD_POSITION: {
      // A narrow command, not `editScaffold`: that one rebuilds the tag from the payload, so a
      // register-created tag - a projection that never carried every column - would come back
      // stripped. Here the server owns the position, the box and the source-360 link, and touches
      // nothing else (AVA-492).
      //
      // The source values go over the wire exactly as the picker produces them
      // (`POSITION_PICK_SOURCE`); the endpoint takes that vocabulary as-is.
      const { source, imageId } = options;
      return new Promise((resolve, reject) => {
        // Checked before the request rather than by the server's 400: a command that cannot name
        // its leg, or a 360 pick that cannot name its scan, is a producer bug and reads as one.
        if (source !== POSITION_PICK_SOURCE.MODEL_3D && source !== POSITION_PICK_SOURCE.PANO_360) {
          reject(new Error(`Unsupported position pick source: ${source}`));
          return;
        }
        if (source === POSITION_PICK_SOURCE.PANO_360 && !imageId) {
          reject(new Error('A 360 position pick must name the image it was clicked in'));
          return;
        }
        postDataAsync(
          'api/scaffolds/setPosition',
          {
            id: obj.id,
            assetId,
            position: toVector3Payload(obj.position),
            scale: toVector3Payload(obj.scale),
            max: toVector3Payload(obj.max),
            source,
            imageId: imageId ?? null,
            // The picture of the placement. The route has always accepted it
            // (SetScaffoldPositionCommand.Img64); this body simply never sent one, so the card
            // kept showing whatever image the scaffold had before it was ever placed.
            img64: img64 ?? null,
          },
          (response) => {
            try {
              // Same legacy raw-tag shape `editScaffold` returns, so the caller gets a hydrated tag.
              const tag = Object.assign(new ForgeScaffold(), response);
              tag.parseContent();
              resolve(tag);
            } catch (err) {
              reject(scaffoldWriteRefreshError('set-position', obj, assetId, response, err));
            }
          },
          reject,
        );
      });
    }
    case editType.CLEAR_SCAFFOLD_POSITION: {
      // The mirror of SET_SCAFFOLD_POSITION and promise-returning for the same reason: the
      // server drops the position and the source-360 link and keeps the box dimensions, so the
      // tag can be placed again from either viewer.
      return new Promise((resolve, reject) => {
        postDataAsync(
          'api/scaffolds/clearPosition',
          { id: obj.id, assetId },
          (response) => {
            try {
              const tag = Object.assign(new ForgeScaffold(), response);
              tag.parseContent();
              resolve(tag);
            } catch (err) {
              reject(scaffoldWriteRefreshError('clear-position', obj, assetId, response, err));
            }
          },
          reject,
        );
      });
    }
    case editType.DELETE_SCAFFOLDS:
      postDataAsync('api/scaffolds/deleteScaffolds', {
        assetId: store.state.assetId,
        guids: obj.map((o) => o.id),
      }, callback, onError);
      break;
  }
}
export function saveAvaInspectTags(obj, changeType, img64, callback, onError) {
  let dbIds = (obj.children ?? []).map((e) => e.dbId);
  let dbIdChain = [];
  if (store.state.viewer) {
    dbIdChain = dbIds.reduce((acc, dbId) => {
      let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
      return acc.concat(chain);
    }, []);
  }

  switch (changeType) {
    case editType.CREATE_AVA_INSPECT:
      postDataAsync(
        'api/tags/ava-inspect/createAvaInspect',
        { assetId: store.state.assetId, tag: obj, dbIdChain, img64: img64 },
        (tag) => {
          obj = Object.assign(new ForgeAvaInspect(), tag);
          obj.parseContent();
          if (callback) callback(obj);
        },
        onError,
      );
      break;
    case editType.EDIT_AVA_INSPECT:
      postDataAsync(
        'api/tags/ava-inspect',
        { assetId: store.state.assetId, tag: obj, img64: img64 },
        (tag) => {
          tag = Object.assign(new ForgeAvaInspect(), tag);
          tag.parseContent();
          obj.setSameParameters(tag);
          if (callback) callback(tag);
        },
        onError,
      );
      break;
    case editType.DELETE_AVA_INSPECT:
      postDataAsync(
        'api/tags/ava-inspect',
        { assetId: store.state.assetId, guids: obj.map((o) => o.id) },
        () => {
          if (callback) callback();
        },
        onError,
      );
      break;
  }
}
export function saveTagCategory(name, color, callback, onError) {
  var info = {
    assetId: store.state.assetId,
    name: name,
    color: color,
  };
  postData('api/categories/editCategories', info, callback, onError);
}
export function saveState(state, callBack, onError) {
  var info = {
    state: JSON.stringify(state),
  };
  postData('api/states/addState', info, (id) => {
    callBack(id);
  }, onError);
}
export function fetchState(id, callback) {
  HttpService.get({
    url: `api/states/getState?id=${id}`,
  })
    .then((response) => {
      var state = !response.data ? null : JSON.parse(response.data.state);
      callback(state);
    })
    .catch((error) => {
      debugError('mongoUtils', 'Error fetching state:', error);
      callback(null);
    });
}
export function getTagsAnnotationsScaffolds() {
  HttpService.get({
    url: `api/getTagsAnnotationsScaffolds`,
    params: {
      assetId: store.state.assetId,
    },
  })
    .then((response) => {
      debugLog('mongoUtils', 'Tags/Annotations/Scaffolds response:', response.data);
    })
    .catch((error) => {
      debugError('mongoUtils', 'Error fetching tags/annotations/scaffolds:', error);
    });
}

export function postData(
  url,
  data,
  onSuccess,
  onError,
  onFinally,
  contentType = 'application/json',
  headers = {},
) {
  forgePost(url, data, false, onSuccess, onError, onFinally, contentType, headers);
}
export function postDataAsync(
  url,
  data,
  onSuccess,
  onError,
  onFinally,
  contentType = 'application/json',
  headers = {},
  config = {},
) {
  forgePost(url, data, true, onSuccess, onError, onFinally, contentType, headers, config);
}
export function forgePost(
  url,
  data,
  async,
  onSuccess,
  onError,
  onFinally,
  contentType = 'application/json',
  headers = {},
  config = {},
) {
  HttpService.post({
    url: url,
    data: data,
    headers,
    ...config,
  })
    .then((response) => {
      if (onSuccess) onSuccess(response.data);
    })
    .catch((error) => {
      if (onError) onError(error);
    })
    .finally((e) => {
      if (onFinally) onFinally(e);
    });
}
/**
 * @param {string} url
 * @param {(data: any) => void} callback - Receives the response data, or null on any error
 * @param {Object} [headers]
 * @param {Object} [config] - Extra axios options, e.g. FACILITY_REQUEST for facility-wide loads
 */
export function forgeFetch(url, callback, headers = {}, config = {}) {
  HttpService.get({
    url: url,
    headers,
    ...config,
  })
    .then((response) => {
      callback(response.data);
    })
    .catch((error) => {
      callback(null);
    });
}
/** forgeFetch for a facility-wide load that must survive navigation inside the facility. */
export function forgeFetchFacility(url, callback, headers = {}) {
  forgeFetch(url, callback, headers, FACILITY_REQUEST);
}
export function mongoToSql() {
  HttpService.post({
    url: `api/mongoToSql/translate`,
  })
    .then((response) => {
      debugLog('mongoUtils', 'Mongo to SQL translation response:', response.data);
    })
    .catch((error) => {
      debugError('mongoUtils', 'Error in mongo to SQL translation:', error);
    });
}
export function fetchProjectModelsInfo(assetId, callback) {
  HttpService.get({
    url: `api/forge/projects/getProjectModelsInfo`,
    params: {
      assetId: assetId,
    },
  })
    .then((response) => {
      var models = response.data;
      if (models == null) return;
      callback(models);
    })
    .catch((error) => {
      debugError('mongoUtils', 'Error fetching project models info:', error);
    });
}
export function fetchUserSettings() {
  const userId = store.getters['ACCOUNT/GET_USER']?.id;

  HttpService.get({
    url: `api/forge/settings/getUserSettings`,
    params: {
      userId: userId,
    },
    ...FACILITY_REQUEST,
  })
    .then((response) => {
      let settings = response.data;

      if (!settings) {
        store.state.userSettings = new UserSettings();
        store.state.userSettings.id = userId;
        return;
      }

      settings = Object.assign(new UserSettings(), settings);

      if (settings.displaySettings) {
        settings.displaySettings = Object.assign(new DisplaySettings(), settings.displaySettings);
        if (settings.displaySettings.pointCloudDisplay) {
          settings.displaySettings.pointCloudDisplay = Object.assign(
            new PointCloudDisplay(),
            settings.displaySettings.pointCloudDisplay,
          );
        } else {
          settings.displaySettings.pointCloudDisplay = new PointCloudDisplay();
        }

        if (settings.displaySettings.quickSettingsDisplay) {
          settings.displaySettings.quickSettingsDisplay = Object.assign(
            new QuickSettingsDisplay(),
            settings.displaySettings.quickSettingsDisplay,
          );
        } else {
          settings.displaySettings.quickSettingsDisplay = new QuickSettingsDisplay();
        }
      } else {
        settings.displaySettings = new DisplaySettings();
      }

      settings.displaySettings.renderFPS =
        localStorage.getItem(DisplaySettings.fpsLocalStorageKey) ?? 8;

      // Must stay AFTER the Object.assign above. That assign copies an explicit null
      // from the server payload over the constructor default, and the picker in
      // displaySettings.vue renders a null value as black rather than grey.
      settings.displaySettings.viewerBackgroundColor = readStoredViewerBackground();

      settings.id = userId;
      store.state.userSettings = settings;

      // Force point cloud settings to reapply with newly fetched values
      const mainExt = store.state.forgeService?.getMainExtension?.();
      if (mainExt) mainExt.resetPointCloudSettingsCache();

      if (store.state.userSettings.displaySettings && store.state.viewer) {
        setModelVisibility(store.state.viewer, store.state.viewer.model, store.state.userSettings.displaySettings.showMasterModel);
        // Settings can arrive after GEOMETRY_LOADED_EVENT has already painted the
        // default — the same race setModelVisibility is re-applied here for.
        applyViewerBackground(
          store.state.viewer,
          store.state.userSettings.displaySettings.viewerBackgroundColor,
        );
      }
    })
    .catch((error) => {
      debugError('mongoUtils', 'Error fetching user settings:', error);
      store.state.userSettings = new UserSettings();
      store.state.userSettings.id = userId;
    });
}
export function testTranslateDataInAzure() {
  HttpService.post({
    url: `api/forge/azure/translateData`,
  })
    .then((response) => {
      debugLog('mongoUtils', 'Azure translate data response:', response.data);
    })
    .catch((error) => {
      debugError('mongoUtils', 'Error translating data in Azure:', error);
    });
}
export function deletePointCloud() {
  HttpService.post({
    url: `api/forge/azure/deletePointCloud`,
  })
    .then((response) => {
      debugLog('mongoUtils', 'Delete point cloud response:', response.data);
    })
    .catch((error) => {
      debugError('mongoUtils', 'Error deleting point cloud:', error);
    });
}

export async function getJSON(url) {
  return await HttpService.get({
    url: url,
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      debugError('mongoUtils', 'Error fetching JSON:', error);
      return null;
    });
}

//Stamps
export async function getAllMarkupStamps() {
  var url = 'api/forge/stamps/getMarkupStamps';
  return await getJSON(url);
}

export function saveMarkupStamp(stamp, callback) {
  var url = 'api/forge/stamps/saveMakrupStamp';
  postData(url, stamp, callback);
}

export function deleteMarkupStamp(stamp) {
  var url = `api/forge/stamps/deleteMarkupStamp/${stamp.id}`;
  HttpService.delete({
    url: url,
  });
}

//Symbols
export async function getAllMarkupSymbols() {
  var url = 'api/forge/symbols/getMarkupSymbols';
  return await getJSON(url);
}

export function saveMarkupSymbol(symbol) {
  var url = 'api/forge/symbols/saveMakrupSymbol';
  var savedSymbol = null;
  postData(url, symbol, (res) => {
    savedSymbol = res;
  });
  return savedSymbol;
}

export function deleteMarkupSymbol(symbol) {
  var url = `api/forge/symbols/deleteMarkupSymbol/${symbol.id}`;
  HttpService.delete({
    url: url,
  });
}

//ForgeAttachment
export async function getAttachment(id) {
  var url = `api/forge/attachments/getAttachment/${id}`;
  var attachmentJson = await getJSON(url);
  var attachment = Object.assign(new ForgeAttachment(), attachmentJson);

  if (!attachment.pagesMarkups) attachment.pagesMarkups = [];
  attachment.pagesMarkups = attachment.pagesMarkups.map((e) =>
    Object.assign(new ForgePageMarkup(), e),
  );
  attachment.pagesMarkups.forEach((m) => {
    if (m.cropBox) m.cropBox = Object.assign(new THREE.Box3(m.cropBox.min, m.cropBox.max));
  });
  return attachment;
}

/**
 * The markups (drawings and links) of a document. Links are per version: pass
 * `{ revisionId }` for a given version; without it the current version's are returned.
 * Origin callers pass context/content ids as further arguments; they are ignored.
 */
export async function getAttachmentByReference(
  documentId,
  options,
) {
  var url = `api/forge/attachments/${documentId}`;
  var revisionId = options && typeof options === 'object' ? options.revisionId || null : null;
  var attachmentJson;
  try {
    // A version nobody has marked up yet answers 204 with no body: start a new attachment.
    attachmentJson = (await HttpService.get({ url, params: revisionId ? { revisionId } : undefined })).data || null;
  } catch (err) {
    // Older API versions answered 404 for the same case.
    if (err?.response?.status !== 404) throw err;
    attachmentJson = null;
  }
  var attachment = Object.assign(new ForgeAttachment(), attachmentJson);

  if (!attachment.pagesMarkups) attachment.pagesMarkups = [];
  attachment.pagesMarkups = attachment.pagesMarkups.map((e) =>
    Object.assign(new ForgePageMarkup(), e),
  );
  attachment.pagesMarkups.forEach((m) => {
    if (m.cropBox) m.cropBox = Object.assign(new THREE.Box3(m.cropBox.min, m.cropBox.max));
  });
  return attachment;
}

export async function getAttachmentsByReference(referenceDocumentIds, referenceContextId) {
  var query = 'referenceDocumentIds=' + referenceDocumentIds.join('&referenceDocumentIds=');
  var url = `api/forge/attachments/getAttachmentsByReference?${query}&referenceContextId=${referenceContextId}`;
  var attachmentsJson = await getJSON(url);
  var attachments = [];
  if (attachmentsJson && attachmentsJson.length > 0) {
    for (let i = 0; i < attachmentsJson.length; i++) {
      const attachmentJson = attachmentsJson[i];
      var attachment = Object.assign(new ForgeAttachment(), attachmentJson);
      if (!attachment.pagesMarkups) attachment.pagesMarkups = [];
      attachment.pagesMarkups = attachment.pagesMarkups.map((e) =>
        Object.assign(new ForgePageMarkup(), e),
      );
      attachment.pagesMarkups.forEach((m) => {
        if (m.cropBox) m.cropBox = Object.assign(new THREE.Box3(m.cropBox.min, m.cropBox.max));
      });
      attachments.push(attachment);
    }
  }
  return attachments;
}

export async function getAttachments(ids) {
  var query = 'ids=' + ids.join('&ids=');
  var url = `api/forge/attachments/getAttachments?${query}`;
  var attachmentsJson = await getJSON(url);
  var attachments = [];
  if (attachmentsJson && attachmentsJson.length > 0) {
    for (let i = 0; i < attachmentsJson.length; i++) {
      const attachmentJson = attachmentsJson[i];
      var attachment = Object.assign(new ForgeAttachment(), attachmentJson);
      if (!attachment.pagesMarkups) attachment.pagesMarkups = [];
      attachment.pagesMarkups = attachment.pagesMarkups.map((e) =>
        Object.assign(new ForgePageMarkup(), e),
      );
      attachment.pagesMarkups.forEach((m) => {
        if (m.cropBox) m.cropBox = Object.assign(new THREE.Box3(m.cropBox.min, m.cropBox.max));
      });
      attachments.push(attachment);
    }
  }
  return attachments;
}

export function postAttachment(attachment, callback) {
  var url = `api/forge/attachments/addAttachment`;
  // Without documentRevisionId the API stamps the current version. Markups started while an
  // older version is open belong to that version.
  if (attachment && !attachment.documentRevisionId && attachment.viewRevisionId) {
    attachment.documentRevisionId = attachment.viewRevisionId;
  }
  postData(url, attachment, callback);
  return attachment;
}

export function editAttachment(attachment) {
  var url = `api/forge/attachments/editAttachment`;
  var result = false;
  HttpService.put({
    url: url,
    data: attachment,
  }).then((response) => {
    result = attachment;
  });
  return result;
}

export function deleteAttachment(id) {
  var url = `api/forge/attachments/deleteAttachment/${id}`;
  var response = false;
  HttpService.delete({
    url: url,
  }).then(() => {
    response = true;
  });
  return response;
}

export function fetchAssetModel(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/assets/fetchForgeModel?assetId=${assetId}`, (model) => {
      resolve(model);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetBookmarks(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/forge/dashboard/getAllBookmarks?assetId=${assetId}`, (bookmarks) => {
      bookmarks ??= [];
      let cameras = bookmarks.map((o) => Object.assign(new ForgeCamera(), o));
      resolve(cameras);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetModels(assetId, selectedModel) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/forge/models/getAssetSmallModels?assetId=${assetId}`, (models) => {
      models ??= [];
      let assetModels = models
        .filter((m) => m.name !== selectedModel)
        .map((o) => Object.assign(new ForgeModelVersion(), o));
      resolve(assetModels);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetPointCloudModels(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/forge/pointCloud/getAllPointCloudModels?assetId=${assetId}`, (models) => {
      models ??= [];
      var pointClouds = models.map((o) => Object.assign(new PointCloudModelInfo(), o));
      resolve(pointClouds);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetPointCloudDownloads(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/pointClouds/getAllPointCloudDownload?assetId=${assetId}`, (pcs) => {
      pcs ??= [];
      let result = pcs.map((o) => Object.assign(new PointCloudDownload(), o));
      resolve(result);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetCategories(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/forge/categories/getAllCategories?assetId=${assetId}`, (categories) => {
      categories ??= [];
      resolve(categories);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetProjects(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/forge/projects/getAssetProjects?assetId=${assetId}`, (projects) => {
      projects ??= [];
      resolve(projects);
    });
  });
}

export function fetchAssetNormalEquipment(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/equipments/getAllNormalEquipment?assetId=${assetId}`, (equipments) => {
      equipments ??= [];
      let tags = equipments.map((o) => Object.assign(new ForgeEquipment(), o));
      tags.forEach((o) => o.parseContent());
      resolve(tags);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetManualPipelines(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/tags/pipeline/getAllManualPipelines?assetId=${assetId}`, (pipelines) => {
      pipelines ??= [];
      let tags = pipelines.map((o) => Object.assign(new ForgePipeline(), o));
      tags.forEach((o) => o.parseContent());
      resolve(tags);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetManualStructural(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/tags/structural/getAllManualStructural?assetId=${assetId}`, (structural) => {
      structural ??= [];
      let tags = structural.map((o) => Object.assign(new ForgeStructural(), o));
      tags.forEach((o) => o.parseContent());
      resolve(tags);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetAnnotations(assetId, userId) {
  return new Promise((resolve, reject) => {
    forgeFetch(
      `api/annotations/getAllAssetAnnotation?assetId=${assetId}&userId=${userId}`,
      (annotations) => {
        annotations ??= [];
        let result = annotations.map((o) => Object.assign(new ForgeAnnotation(), o));
        result.forEach((o) => o.parseContent());
        resolve(result);
      },
      facilityHeaders(),
      FACILITY_REQUEST,
    );
  });
}

export function fetchAssetScaffold(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/scaffolds/getAllAssetScaffolds?assetId=${assetId}`, (data) => {
      data ??= [];
      let scaffolds = data.map((o) => Object.assign(new ForgeScaffold(), o));
      scaffolds.forEach((o) => {
        //TODO: remove this later
        if (!o.type) o.type = 'scaffold';

        o.position = Object.assign(new THREE.Vector3(), o.position);
        o.scale = Object.assign(new THREE.Vector3(), o.scale);
        o.max = Object.assign(new THREE.Vector3(), o.max);
        o.attachments = ForgeAttachment.parseAttachments(o.attachments);
        o.parseComments();
      });
      resolve(scaffolds);
    }, {}, FACILITY_REQUEST);
  });
}

export function fetchAssetInspections(assetId) {
  return new Promise((resolve, reject) => {
    forgeFetch(`api/forge/inspections/getAllAssetInspections?assetId=${assetId}`, (data) => {
      data ??= [];
      let inspections = data.map((o) => Object.assign(new ForgeInspection(), o));
      inspections.forEach((o) => o.parseContent());
      store.state.forgeService.updateAMISTagsColors(inspections);
      resolve(inspections);
    });
  });
}
